/**
 * Build sec_groups_apps route catalog from routes.js (primary source of truth).
 *
 * Optional fields on route entries in routes.js:
 * - secScope: override inferred access scope (stored in sec_groups_apps.app_name)
 * - secAccess: false to exclude from scope catalog (e.g. demo pages)
 */

/** @param {string|null|undefined} routePath */
export function routePathToSecAppName(routePath) {
  return String(routePath || "")
    .trim()
    .replace(/^\/+/, "")
    .replace(/\/+$/, "");
}

const EXCLUDED_ROUTE_PATHS = new Set([
  "/logout",
  "/login",
  "/loggedout",
  "/authentication/sign-in",
  "/authentication/sign-up",
]);

/** Routes that must remain available regardless of sec_groups_apps / scope access. */
export const ALWAYS_ACCESSIBLE_ROUTE_PATHS = new Set(["logout", "loggedout"]);

/**
 * @param {string|null|undefined} routePath
 * @returns {boolean}
 */
export function isAlwaysAccessiblePath(routePath) {
  return ALWAYS_ACCESSIBLE_ROUTE_PATHS.has(routePathToSecAppName(routePath));
}

/**
 * @param {{ route?: string, key?: string, alwaysAccessible?: boolean }|null|undefined} routeConfig
 * @returns {boolean}
 */
export function isAlwaysAccessibleRouteConfig(routeConfig) {
  if (!routeConfig) return false;
  if (routeConfig.alwaysAccessible === true) return true;
  if (routeConfig.key === "logout") return true;
  return isAlwaysAccessiblePath(routeConfig.route);
}

/** Flat AdminZone paths whose scope key differs from the first URL segment. */
const PATH_ROOT_SCOPE = {
  "tickets-list": "tickets",
  "whatsapp-inbox": "whatsapp",
  "whatsapp-chat": "whatsapp",
  "whatsapp-send-to-one": "whatsapp",
  "whatsapp-send-outage": "whatsapp",
  "whatsapp-send-to-all": "whatsapp",
  "version-history": "system",
  help: "system",
};

/**
 * @param {string|null|undefined} routePath
 * @param {string|null|undefined} sectionKey
 * @param {string|null|undefined} secScopeOverride
 * @returns {string}
 */
export function resolveScopeForRoute(routePath, sectionKey, secScopeOverride) {
  const override = routePathToSecAppName(secScopeOverride);
  if (override) return override;

  const path = routePathToSecAppName(routePath);
  const section = routePathToSecAppName(sectionKey);
  if (!path) return section;

  const root = path.split("/")[0];
  if (PATH_ROOT_SCOPE[root]) return PATH_ROOT_SCOPE[root];
  if (root && root !== section) return root;
  return section || root;
}

/**
 * @param {object} item
 * @param {{ key?: string, name?: string, protected?: boolean }|null|undefined} section
 * @param {boolean} [effectiveProtected=false]
 * @returns {boolean}
 */
export function shouldIncludeRouteInAccessCatalog(item, section, effectiveProtected = false) {
  if (item?.secAccess === false) return false;
  if (item?.type === "auth") return false;
  if (typeof item?.route !== "string" || !item.route.trim()) return false;
  if (EXCLUDED_ROUTE_PATHS.has(item.route)) return false;
  if (section?.key === "demo") return false;

  const isProtected =
    effectiveProtected ||
    item?.protected === true ||
    item?.secAccess === true ||
    section?.protected === true;
  if (!isProtected) return false;
  return true;
}

/**
 * @param {object} item
 * @param {{ key?: string, name?: string }} section
 * @returns {{ section: string, label: string, route: string, appName: string, scope: string }}
 */
export function toRouteAccessCatalogEntry(item, section) {
  const route = String(item.route).trim();
  const scope = resolveScopeForRoute(route, section?.key, item.secScope);
  return {
    section: section?.name || "Other",
    label: String(item.name || route),
    route,
    appName: routePathToSecAppName(route),
    scope,
  };
}

/**
 * @param {Array<object>|null|undefined} routeTree
 * @returns {Array<{ section: string, label: string, route: string, appName: string, scope: string }>}
 */
export function buildRouteAccessCatalogFromRoutes(routeTree) {
  /** @type {Array<{ section: string, label: string, route: string, appName: string, scope: string }>} */
  const entries = [];

  const walk = (nodes, section = null) => {
    (Array.isArray(nodes) ? nodes : []).forEach((item) => {
      if (item?.type === "title" || item?.type === "divider") return;

      const isNavGroup = item?.type === "collapse" && item?.key && item?.name;
      const sectionProtected = section?.protected === true;
      const itemProtected = item?.protected === true || item?.secAccess === true;
      const inheritedProtected = sectionProtected || itemProtected;

      const currentSection = isNavGroup
        ? { key: item.key, name: item.name, protected: inheritedProtected }
        : section;

      if (Array.isArray(item.collapse)) {
        walk(item.collapse, currentSection);
      }

      if (!shouldIncludeRouteInAccessCatalog(item, currentSection, inheritedProtected)) return;

      entries.push(
        toRouteAccessCatalogEntry(item, currentSection || { key: item.key, name: item.name })
      );
    });
  };

  walk(routeTree, null);
  return entries;
}

/**
 * @param {string|null|undefined} value
 * @returns {string}
 */
function titleCaseScope(value) {
  return String(value || "")
    .split(/[-_/]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

/**
 * @param {ReturnType<typeof buildRouteAccessCatalogFromRoutes>} catalog
 * @returns {Array<{ scope: string, section: string, label: string, description: string }>}
 */
export function buildSecAccessScopesFromCatalog(catalog) {
  const scopeOrder = [];
  /** @type {Map<string, { scope: string, section: string, routeLabels: string[] }>} */
  const scopeMap = new Map();

  (Array.isArray(catalog) ? catalog : []).forEach((entry) => {
    const scope = routePathToSecAppName(entry?.scope);
    if (!scope) return;

    if (!scopeMap.has(scope)) {
      scopeOrder.push(scope);
      scopeMap.set(scope, {
        scope,
        section: String(entry.section || "Other").trim() || "Other",
        routeLabels: [],
      });
    }

    const item = scopeMap.get(scope);
    const routeLabel = String(entry.label || "").trim();
    if (routeLabel && !item.routeLabels.includes(routeLabel)) {
      item.routeLabels.push(routeLabel);
    }
  });

  return scopeOrder.map((scope) => {
    const item = scopeMap.get(scope);
    const section = item.section;
    const scopeTitle = titleCaseScope(scope);
    const label =
      scopeTitle.toLowerCase() === section.toLowerCase() || scope === section.toLowerCase()
        ? section
        : scopeTitle;
    const routeLabels = item.routeLabels;
    const description =
      routeLabels.length < 1
        ? `All ${label} routes`
        : routeLabels.length <= 5
          ? routeLabels.join(", ")
          : `${routeLabels.slice(0, 4).join(", ")}, and ${routeLabels.length - 4} more`;

    return { scope, section, label, description };
  });
}
